import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./ForceEquipment.css";
import { useProducts, type ForceEquipmentSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const ForceEquipment: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products, loading, error } = useProducts<ForceEquipmentSpecs>('force-equipment', editMode);
  const navigate = useNavigate();
  const dynamometers = products.filter(p => p.specs?.subType == "dynamometer");
  const indicators = products.filter(p => p.specs?.subType == "indicator");
  const tensionMeters = products.filter(p => p.specs?.subType == "quickcheck");
  const loadCells = products.filter(p => p.specs?.subType == "loadcell");
  const other = products.filter(p => p.specs?.subType == "other");

  const applications = [
    {
      title: "Industria del Acero",
      description: "Manejo de cargas pesadas y control de procesos en plantas siderúrgicas.",
    },
    {
      title: "Grúas Industriales",
      description: "Medición de cargas en grúas fijas y viajeras para operaciones seguras.",
    },
    {
      title: "Servicios Públicos",
      description: "Instalación y mantenimiento de líneas eléctricas y telefónicas.",
    },
    {
      title: "Laboratorios",
      description: "Pruebas de materiales y control de calidad en investigación.",
    },
    {
      title: "Manufactura",
      description: "Control de procesos y verificación de fuerza en líneas de producción.",
    },
    {
      title: "Mantenimiento",
      description: "Verificación rápida de tensión en cables y estructuras.",
    },
  ];

  const consultingFeatures = [
    "Asesoría técnica especializada en equipos de fuerza",
    "Soluciones personalizadas adaptadas a su aplicación",
    "Soporte integral desde selección hasta instalación",
  ];

  const overviewFeatures = [
    "Dinamómetros desde 200 kg hasta 250,000 kg",
    "Indicadores especializados para captura de datos",
    "Tensiómetros de medición rápida",
    "Celdas de carga versátiles",
    "Opciones electrónicas y mecánicas",
    "Soluciones para grúas e industria del acero",
  ];

  return (
    <div className="fe-page">
      {error && <div className="fe-status-bar fe-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="fe-hero">
        <div className="container fe-hero-content">
          <nav className="fe-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="fe-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="fe-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Equipo de Fuerza</span>
          </nav>
          <h1 className="fe-hero-title">Equipo de Fuerza</h1>
          <p className="fe-hero-subtitle">
            Amplia variedad de equipos de fuerza para medición y control en
            aplicaciones industriales.
          </p>
          {authType === 'internal' && (
            <button
              className={`fe-btn-edit-mode${editMode ? ' fe-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="fe-hero-image" aria-hidden="true">
          <img src="/images/force-equipment.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="fe-overview">
        <div className="container">
          <div className="fe-overview-grid">
            <div className="fe-overview-text">
              <span className="fe-label">Descripción general</span>
              <h2>Medición precisa de fuerza</h2>
              <p>
                Tenemos una amplia variedad de equipos de fuerza
                incluyendo dinamómetros electrónicos y mecánicos, indicadores de fuerza, tensiómetros
                y celdas de carga especializadas para aplicaciones industriales de medición y control.
              </p>
            </div>
            <div>
              <ul className="fe-features-list" aria-label="Características principales">
              {overviewFeatures.map((feature, index) => (
                <li key={index} className="fe-feature-item">
                  <svg className="fe-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            </div>
          </div>
        </div>
      </section>

      

      {/* ── Dynamometers ─────────────────────────────────────── */}
      <section className="fe-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Dinamómetros</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/force-equipment')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : dynamometers.map((dynamometer, index) => (
                <ProductCard
                  key={index}
                  reviewed={dynamometer.reviewed}
                  endOfLife={dynamometer.endOfLife}
                  productId={dynamometer.productId}
                  name={dynamometer.name}
                  badge={dynamometer.specs?.type ?? undefined}
                  description={dynamometer.specs?.description ?? undefined}
                  image={dynamometer.image}
                  onRevisar={editMode && dynamometer.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(dynamometer.specs?.capacities?.length ? [{ icon: '📦', label: 'Capacidades', items: (dynamometer.specs?.capacities ?? []).filter((x): x is string => x != null) }] : []),
                    ...(dynamometer.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (dynamometer.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(dynamometer.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (dynamometer.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Force Indicators ─────────────────────────────────── */}
      <section className="fe-indicators">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Indicadores de Fuerza</h2>
            </div>
            <div className="models-grid">
              {indicators.map((indicator, index) => (
                <ProductCard
                  key={index}
                  reviewed={indicator.reviewed}
                  endOfLife={indicator.endOfLife}
                  productId={indicator.productId}
                  name={indicator.name}
                  badge={indicator.specs?.model ?? undefined}
                  description={indicator.specs?.description ?? undefined}
                  image={indicator.image}
                  onRevisar={editMode && indicator.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(indicator.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (indicator.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(indicator.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (indicator.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Tension Meters ───────────────────────────────────── */}
      <section className="fe-tension">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Tensiómetros</h2>
            </div>
            <div className="models-grid">
              {tensionMeters.map((meter, index) => (
                <ProductCard
                  key={index}
                  reviewed={meter.reviewed}
                  endOfLife={meter.endOfLife}
                  productId={meter.productId}
                  name={meter.name}
                  description={meter.specs?.description ?? undefined}
                  image={meter.image}
                  onRevisar={editMode && meter.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(meter.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (meter.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(meter.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (meter.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Other ────────────────────────────────────────────── */}
      <section className="fe-other">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Otros</h2>
            </div>
            <div className="models-grid">
              {other.map((item, index) => (
                <ProductCard
                  key={index}
                  reviewed={item.reviewed}
                  endOfLife={item.endOfLife}
                  productId={item.productId}
                  name={item.name}
                  description={item.specs?.description ?? undefined}
                  image={item.image}
                  onRevisar={editMode && item.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(item.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (item.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(item.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (item.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Load Cells ───────────────────────────────────────── */}
      <section className="fe-loadcells">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Celdas de Carga</h2>
            </div>
            <div className="models-grid">
              {loadCells.map((cell, index) => (
                <ProductCard
                  key={index}
                  reviewed={cell.reviewed}
                  endOfLife={cell.endOfLife}
                  productId={cell.productId}
                  name={cell.name}
                  badge={cell.specs?.type ?? undefined}
                  description={cell.specs?.description ?? undefined}
                  image={cell.image}
                  onRevisar={editMode && cell.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(cell.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (cell.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="fe-applications">
        <div className="container">
          <span className="fe-label fe-label--light">Sectores</span>
          <h2 className="fe-section-title fe-section-title--light">Aplicaciones Industriales</h2>
          <div className="fe-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="fe-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Consulting ───────────────────────────────────────── */}
      <section className="fe-consulting">
        <div className="container">
          <div className="fe-consulting-inner">
            <div className="fe-consulting-header">
              <span className="fe-label">Asesoría</span>
              <h2 className="fe-section-title">Consulta Especializada</h2>
              <p>
                Para equipos específicos y aplicaciones particulares, recomendamos
                contactar directamente con nuestros especialistas para obtener la
                solución más adecuada a sus necesidades de medición de fuerza.
              </p>
            </div>
            <ul className="fe-consulting-list" aria-label="Servicios de consulta">
              {consultingFeatures.map((feature, index) => (
                <li key={index} className="fe-consulting-item">
                  <svg className="fe-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForceEquipment;
